const FINAL_WORKER_STATES = new Set<ServiceWorkerState>(['installed', 'activated', 'redundant'])

type Listener = (isDownloading: boolean) => void

/**
 * Tracks when an update worker is downloading/pre-caching.
 * Only considers updates when an active worker already exists.
 */
export function watchSwUpdateDownload(
  registration: ServiceWorkerRegistration | undefined,
  onChange: Listener,
): () => void {
  if (!registration) return () => {}

  let detachInstalling: (() => void) | null = null

  const stopTrackingInstalling = () => {
    if (!detachInstalling) return
    detachInstalling()
    detachInstalling = null
  }

  const trackInstalling = (worker: ServiceWorker) => {
    stopTrackingInstalling()
    onChange(true)

    const onStateChange = () => {
      if (!FINAL_WORKER_STATES.has(worker.state)) return
      onChange(false)
      stopTrackingInstalling()
    }

    worker.addEventListener('statechange', onStateChange)
    detachInstalling = () => worker.removeEventListener('statechange', onStateChange)
  }

  const handleUpdateFound = () => {
    const worker = registration.installing
    if (!worker) return
    // Ignore first SW install: no previous version to update.
    if (!registration.active) return
    trackInstalling(worker)
  }

  registration.addEventListener('updatefound', handleUpdateFound)
  handleUpdateFound()

  return () => {
    registration.removeEventListener('updatefound', handleUpdateFound)
    stopTrackingInstalling()
  }
}
